/** /tag_categories/ — tenant-scoped tag-category catalog (list + CRUD).
 *
 * Hand-authored wrapper. A category's effective schema is either an inline
 * `schema` or a referenced reusable `schema_ref` (the console encourages the
 * ref). `tour_operator` is nullable (system default). Deletes 409 when the
 * category still has live tags (a delete would cascade them).
 */
import type { TagSchema } from '@/features/tags/schema/vocabulary';

import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';

export type SchemaSource = 'inline' | 'ref' | 'none';

export interface TagCategorySchemaRef {
  id: number;
  codename: string;
  name: string;
}

export interface TagCategoryListItem {
  id: number;
  tenant: TenantRef | null;
  code: string;
  name: string;
  schema_source: SchemaSource;
  schema_ref: TagCategorySchemaRef | null;
  state_category: boolean;
  is_multitag: boolean;
  /** Tags here mirror an upstream entity and are identified by `custom_id`, not by
   *  name — so two tags in the category may share a display name. */
  external_identity: boolean;
  accounting: boolean;
  billing: boolean;
  tag_count: number;
  created_ts: string;
  updated_ts: string;
}

export interface TagCategoryDetail extends TagCategoryListItem {
  schema: TagSchema | null;
  keyfield: string;
  agroups: NamedRef[];
  used_by: { tags: number };
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  tour_operator_id?: number | string;
  state_category?: boolean;
  is_multitag?: boolean;
  external_identity?: boolean;
  accounting?: boolean;
  billing?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  for (const flag of ['state_category', 'is_multitag', 'external_identity', 'accounting', 'billing'] as const) {
    if (params[flag] !== undefined) usp.set(flag, String(params[flag]));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export interface TagCategoryCreateInput {
  tour_operator: number | null;
  code: string;
  name: string;
  schema_ref?: number | null;
  schema?: TagSchema | null;
  state_category?: boolean;
  is_multitag?: boolean;
  external_identity?: boolean;
  accounting?: boolean;
  billing?: boolean;
  keyfield?: string;
  agroups?: { id: number }[];
}

export interface TagCategoryWriteInput {
  tour_operator?: number | null;
  code?: string;
  name?: string;
  schema?: TagSchema | null;
  schema_ref?: number | null;
  state_category?: boolean;
  is_multitag?: boolean;
  external_identity?: boolean;
  accounting?: boolean;
  billing?: boolean;
  keyfield?: string;
  agroups?: { id: number }[];
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TagCategoryListItem>> {
  return apiGet<PaginatedResponse<TagCategoryListItem>>(`/tag_categories/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<TagCategoryDetail> {
  return apiGet<TagCategoryDetail>(`/tag_categories/${id}/`);
}

export function create(body: TagCategoryCreateInput): Promise<TagCategoryDetail> {
  return apiPost<TagCategoryDetail>('/tag_categories/', body);
}

export function update(id: number, body: TagCategoryWriteInput): Promise<TagCategoryDetail> {
  return apiPatch<TagCategoryDetail>(`/tag_categories/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/tag_categories/${id}/`);
}

/** The standard schema for this category code, when one is published. */
export interface TagCategoryStandard {
  codename: string;
  name: string;
  description: string;
  property_count: number;
  /** Whether this category's `schema_ref` already points at the standard. */
  is_referenced: boolean;
}

export interface TagCategorySchemaDiff {
  code: string;
  schema_source: SchemaSource;
  declared_properties: string[];
  standard: TagCategoryStandard | null;
  missing_from_standard: { name: string; type: string | null; label: string }[];
  type_conflicts: { name: string; standard: string | null; effective: string | null }[];
  undeclared: { name: string; tags: number; sample: string | null }[];
  flat_multilingual: {
    base: string;
    languages: string[];
    tags: number;
    /** false ⇒ the values are invisible to {% tagattr %}. */
    declared_multilingual: boolean;
  }[];
  tags_scanned: number;
  truncated: boolean;
}

/** This category versus the standard schema, and versus its own tag data.
 *
 *  Read-only: the standard is referenced, never merged into a tenant's inline
 *  schema, so adopting it is a deliberate act made on this report.
 */
export function schemaDiff(id: number, sampleLimit?: number): Promise<TagCategorySchemaDiff> {
  const qs = sampleLimit ? `?sample_limit=${sampleLimit}` : '';
  return apiGet<TagCategorySchemaDiff>(`/tag_categories/${id}/schema_diff/${qs}`);
}
